import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Bell, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import EditEntityDialog from '../components/EditEntityDialog';
import RowActions from '../components/RowActions';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'];

export default function Notices() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newN, setNewN] = useState({
    title: '', body: '', category: 'general', severity: 'info',
    building_id: '', valid_until: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [n, b] = await Promise.all([
        axios.get(`${API}/notices`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true })
      ]);
      setItems(n.data); setBuildings(b.data);
    } catch { toast.error('Failed'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/notices`, {
        ...newN,
        building_id: newN.building_id || null,
        valid_until: newN.valid_until ? new Date(newN.valid_until).toISOString() : null
      }, { withCredentials: true });
      toast.success('Notice published');
      setShowNew(false);
      setNewN({ title: '', body: '', category: 'general', severity: 'info', building_id: '', valid_until: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const ack = async (id) => {
    try { await axios.post(`${API}/notices/${id}/ack`, {}, { withCredentials: true }); toast.success('Acknowledged'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete notice?')) return;
    try { await axios.delete(`${API}/notices/${id}`, { withCredentials: true }); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const sevStyle = { info: 'bg-blue-50 border-l-blue-500', warning: 'bg-amber-50 border-l-amber-500', critical: 'bg-red-50 border-l-red-500' };
  const sevBadge = { info: 'bg-blue-100 text-blue-800', warning: 'bg-amber-100 text-amber-800', critical: 'bg-red-100 text-red-800' };

  return (
    <Layout>
      <div data-testid="notices-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('notice.title')}</h1>
            <p className="text-sm text-muted-foreground">{t('notice.subtitle')}</p>
            <p className="text-sm text-muted-foreground">Building notifications, shutdowns, emergencies & circulars</p>
          </div>
          {MANAGE.includes(user?.role) && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild><Button data-testid="new-notice-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />Post Notice</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New Notice / Circular</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <div><Label>Title</Label><Input data-testid="notice-title" value={newN.title} onChange={(e) => setNewN({ ...newN, title: e.target.value })} /></div>
                  <div><Label>Body</Label><Textarea rows={4} data-testid="notice-body" value={newN.body} onChange={(e) => setNewN({ ...newN, body: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Category</Label>
                      <Select value={newN.category} onValueChange={(v) => setNewN({ ...newN, category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General</SelectItem>
                          <SelectItem value="shutdown">Shutdown</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="emergency">Emergency</SelectItem>
                          <SelectItem value="circular">Circular</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Severity</Label>
                      <Select value={newN.severity} onValueChange={(v) => setNewN({ ...newN, severity: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="info">Info</SelectItem>
                          <SelectItem value="warning">Warning</SelectItem>
                          <SelectItem value="critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Building (opt)</Label>
                      <Select value={newN.building_id} onValueChange={(v) => setNewN({ ...newN, building_id: v })}>
                        <SelectTrigger><SelectValue placeholder="All buildings" /></SelectTrigger>
                        <SelectContent>{buildings.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Valid Until</Label><Input type="date" value={newN.valid_until} onChange={(e) => setNewN({ ...newN, valid_until: e.target.value })} /></div>
                  </div>
                  <Button data-testid="submit-notice-btn" onClick={handleCreate} disabled={!newN.title || !newN.body} className="w-full bg-teal-600 hover:bg-teal-700">Publish</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <div className="space-y-3">
            {items.map(n => {
              const acked = n.acknowledged_by?.includes(user?.id);
              return (
                <Card key={n.id} data-testid={`notice-card-${n.id}`} className={`p-5 border-l-4 ${sevStyle[n.severity] || sevStyle.info}`}>
                  <div className="flex justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {n.severity === 'critical' ? <AlertTriangle className="w-4 h-4 text-red-600" /> : <Bell className="w-4 h-4 text-slate-600" />}
                        <h3 className="font-semibold text-foreground">{n.title}</h3>
                      </div>
                      <p className="text-sm text-slate-700 mb-3 whitespace-pre-wrap">{n.body}</p>
                      <div className="flex flex-wrap gap-2 text-xs">
                        <span className={`px-2 py-1 rounded-full font-semibold ${sevBadge[n.severity]}`}>{n.severity}</span>
                        <span className="px-2 py-1 rounded-full bg-slate-100 capitalize">{n.category}</span>
                        <span className="px-2 py-1 rounded-full bg-slate-100">{new Date(n.created_at).toLocaleDateString()}</span>
                        {n.acknowledged_by?.length > 0 && <span className="px-2 py-1 rounded-full bg-green-100 text-green-800">✓ {n.acknowledged_by.length} ack'd</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {!acked ? (
                        <Button data-testid={`ack-notice-${n.id}`} onClick={() => ack(n.id)} size="sm" variant="outline" className="text-green-600 border-green-600"><Check className="w-4 h-4 mr-1" />Acknowledge</Button>
                      ) : (
                        <span className="text-xs text-green-600 font-semibold px-2">✓ Acknowledged</span>
                      )}
                      {MANAGE.includes(user?.role) && (
                        <RowActions
                          testIdPrefix={`notice-${n.id}`}
                          onEdit={() => setEditingId(n.id)}
                          onDelete={() => handleDelete(n.id)}
                          deleteLabel={`notice "${n.title}"`}
                        />
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
            {items.length === 0 && <Card className="p-10 text-center"><Bell className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-muted-foreground">No notices yet</p></Card>}
          </div>
        )}

        {editingId && (
          <EditEntityDialog
            open
            hideTrigger
            onOpenChange={(v) => { if (!v) setEditingId(null); }}
            item={items.find(x => x.id === editingId)}
            endpoint="notices"
            title="Edit Notice"
            onSaved={() => { setEditingId(null); fetchData(); }}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'body', label: 'Body', type: 'textarea', rows: 4 },
              { key: 'category', label: 'Category', type: 'select', options: [
                { value: 'general', label: 'General' },
                { value: 'shutdown', label: 'Shutdown' },
                { value: 'maintenance', label: 'Maintenance' },
                { value: 'emergency', label: 'Emergency' },
                { value: 'circular', label: 'Circular' },
              ]},
              { key: 'severity', label: 'Severity', type: 'select', options: [
                { value: 'info', label: 'Info' },
                { value: 'warning', label: 'Warning' },
                { value: 'critical', label: 'Critical' },
              ]},
              { key: 'building_id', label: 'Building', type: 'select', options: [{ value: '', label: 'All buildings' }, ...buildings.map(b => ({ value: b.id, label: b.name }))] },
            ]}
          />
        )}
      </div>
    </Layout>
  );
}
